import { useMemo } from 'react';
import { Sparkles } from 'lucide-react';

/**
 * SavingsSummaryCard — small, inline card that shows the total dollar
 * savings (list price − deal price) across the checkout cart, plus the
 * charity round-up amount so the consumer sees BOTH benefits together.
 *
 * Cluster 6 P2. Purely presentational; no API calls.
 *
 * Props:
 *   cart      Array of {price, original_price?, quantity, ...}
 *   roundUp   Number - charity round-up amount (optional; default 0)
 */
export default function SavingsSummaryCard({ cart, roundUp = 0 }) {
  const { totalSaved, itemsWithSavings } = useMemo(() => {
    if (!Array.isArray(cart) || cart.length === 0) {
      return { totalSaved: 0, itemsWithSavings: 0 };
    }
    let saved = 0;
    let withSavings = 0;
    for (const item of cart) {
      const orig = Number(item.original_price ?? item.list_price ?? item.regular_price ?? 0);
      const deal = Number(item.price ?? item.deal_price ?? 0);
      const qty = Number(item.quantity ?? 1);
      if (orig > deal && deal > 0) {
        saved += (orig - deal) * qty;
        withSavings += 1;
      }
    }
    return { totalSaved: saved, itemsWithSavings: withSavings };
  }, [cart]);

  if (totalSaved <= 0 && (!roundUp || roundUp <= 0)) return null;

  return (
    <div
      data-testid="savings-summary-card"
      className="rounded-md bg-gradient-to-r from-emerald-50 to-emerald-100/50 border border-emerald-200 px-4 py-3 flex items-center gap-3"
    >
      <div className="w-9 h-9 rounded-full bg-emerald-500/10 flex items-center justify-center shrink-0">
        <Sparkles className="w-5 h-5 text-emerald-600" />
      </div>
      <div className="flex-1 text-sm">
        {totalSaved > 0 && (
          <div>
            <b className="text-emerald-700 text-base" data-testid="savings-summary-amount">
              You save ${totalSaved.toFixed(2)}
            </b>
            <span className="text-slate-600"> on this order</span>
            {itemsWithSavings > 0 && (
              <span className="text-slate-500 text-xs"> ({itemsWithSavings} discounted item{itemsWithSavings === 1 ? '' : 's'})</span>
            )}
          </div>
        )}
        {roundUp > 0 && (
          <div className="text-slate-600 text-xs mt-0.5" data-testid="savings-summary-charity">
            + <b>${Number(roundUp).toFixed(2)}</b> going to charity from round-up.
          </div>
        )}
      </div>
    </div>
  );
}
